import type { WeeklyAnalyticsRow } from "../../types";

export interface WeekTotals {
  week: string;
  created: number;
  completed: number;
  cancelled: number;
  openAtEnd: number;
  storyPoints: number;
  slaMet: number;
  slaTotal: number;
  slaBreached: number;
  inFactory: number;
  medianCycleHours: number | null;
}

export function totalsByWeek(rows: WeeklyAnalyticsRow[]): WeekTotals[] {
  const byWeek = new Map<string, WeekTotals & { cycleSamples: number[] }>();
  for (const row of rows) {
    const entry = byWeek.get(row.weekStart) ?? {
      week: shortWeek(row.weekStart),
      created: 0,
      completed: 0,
      cancelled: 0,
      openAtEnd: 0,
      storyPoints: 0,
      slaMet: 0,
      slaTotal: 0,
      slaBreached: 0,
      inFactory: 0,
      medianCycleHours: null,
      cycleSamples: [],
    };
    entry.created += row.created;
    entry.completed += row.completed;
    entry.cancelled += row.cancelled;
    entry.openAtEnd += row.openAtEnd;
    entry.storyPoints += row.storyPoints;
    entry.slaMet += row.slaMet;
    entry.slaTotal += row.slaTotal;
    entry.slaBreached += row.slaBreached;
    entry.inFactory += row.inFactory;
    if (row.medianCycleHours !== null && row.completed > 0) {
      for (let i = 0; i < row.completed; i += 1) entry.cycleSamples.push(row.medianCycleHours);
    }
    byWeek.set(row.weekStart, entry);
  }
  return [...byWeek.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, entry]) => {
      const sorted = entry.cycleSamples.sort((a, b) => a - b);
      const median = sorted.length ? sorted[Math.floor(sorted.length / 2)]! : null;
      const { cycleSamples: _samples, ...rest } = entry;
      return { ...rest, medianCycleHours: median };
    });
}

export function shortWeek(weekStart: string): string {
  const date = new Date(weekStart);
  return date.toLocaleDateString([], { day: "numeric", month: "short" });
}

export function formatHours(hours: number | null | undefined): string {
  if (hours == null) return "—";
  if (hours < 24) return `${hours.toFixed(hours < 10 ? 1 : 0)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

export function percent(part: number | undefined, total: number | undefined): string {
  if (!total) return "—";
  return `${Math.round(((part ?? 0) / total) * 100)}%`;
}

/** Formats an average run time given in minutes, e.g. `42s`, `3.5m`, `1.2h`. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null) return "—";
  if (minutes < 1) return `${Math.round(minutes * 60)}s`;
  if (minutes < 60) return `${minutes < 10 ? minutes.toFixed(1) : Math.round(minutes)}m`;
  return `${(minutes / 60).toFixed(1)}h`;
}
